import { useCallback, useContext, useEffect, useState } from "react";
import { debounce } from "lodash";
import { useAsync } from "./asyncFunc";
import { getOrderPrice } from "../state_management/epics";
import { useTimeout } from "./useTimeout";
import { cartItem, extrasCartItem } from "../state_management/reducers/cart";
import { orderType } from "../state_management/reducers/order";
import { epics_cart_items, split_cart } from "./cartUtilities";
import { orderDetailsType } from "../state_management/actionTypes";
import { parseDirectOrder } from "../elements/CheckOutPayment/usePayment";
import { CartTotalContext, TotalWithCouponContext } from "../AppProvider";

export type couponErrorTypeEnum =
  | "expired"
  | "not_cover"
  | "exhausted"
  | "not_exist";
export const couponErrorTypeArray: couponErrorTypeEnum[] = [
  "exhausted",
  "expired",
  "not_cover",
  "not_exist"
];
export const useCoupon = ({
  cart,
  extras,
  inactive,
  setOrderDetails,
  orderDetails,
  directData,
  direct,
  pageCount
}: {
  inactive: boolean; // Do you want to inactivate
  cart: cartItem[];
  extras: extrasCartItem[];
  orderDetails: orderType;
  setOrderDetails: (data: orderType) => void;
  directData?: orderDetailsType["data"]; // Date gotten via token and id
  direct: boolean; // Is the session via a cart link url
  pageCount: number;
}) => {
  const time = 3000;
  const [totalWithCoupon, setTotalWithCoupon] = useContext(
    TotalWithCouponContext
  );
  const [cartTotal] = useContext(CartTotalContext);

  const details = direct ? parseDirectOrder(directData) : orderDetails;
  const [couponError, setCouponError] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [couponErrorType, setCouponErrorType] = useState<couponErrorTypeEnum>();
  const [errorBanner, setErrorBanner] = useState(false);
  const [disabledCouponInput, setDisabledCouponInput] = useState(
    direct ? true : false
  );

  const { extra_services, gift_values, services } = epics_cart_items({
    ...split_cart(cart),
    extras
  });

  const isDirectDataLoaded = direct ? !!directData : true;
  const canCall =
    !(inactive || !isDirectDataLoaded) &&
    !!(gift_values.length + services.length);

  const [showDiscount, setShowDiscount] = useState(false);
  // change
  const [localCouponValue, setLocalCouponValue] = useState<string | null>(
    details.coupon_code || ""
  );
  const [localEmail, setLocalEmail] = useState(details.client_email);

  const debouncedRefine = useCallback(
    debounce((localEmail) => {
      setLocalEmail(localEmail);
    }, 400),
    [setLocalEmail, 400]
  );

  useEffect(() => {
    debouncedRefine(details.client_email);
  }, [debouncedRefine, details.client_email]);

  const couponRes = useAsync(
    {
      asyncFunc: getOrderPrice,
      funcParams: {
        details: { ...details, client_email: localEmail },
        coupon_code: canCall ? details.coupon_code : null,
        extra_services,
        services,
        gift_values,
        show_banner: pageCount === 2
      } as Parameters<typeof getOrderPrice>[0],
      canCall,
      immediate: true,
      clearOnError: true
    },
    [
      JSON.stringify([...extra_services, ...services, ...gift_values]),
      localEmail
    ]
  );

  useEffect(() => {
    setIsApplied(false);
    setError(false, undefined);
  }, []);

  useEffect(() => {
    if (isDirectDataLoaded && directData) {
      setDisabledCouponInput(!!directData["coupon code"]);
    }
  }, [isDirectDataLoaded, directData?.id]);

  useEffect(() => {
    if (totalWithCoupon === null) {
      setTotalWithCoupon(cartTotal);
    }
  }, [totalWithCoupon]);

  useEffect(() => {
    if (direct && canCall) {
      setLocalCouponValue(details.coupon_code || "");
    }
  }, [canCall, direct]);

  useEffect(() => {
    if (localCouponValue) {
      setShowDiscount(true);
    }
  }, [localCouponValue]);

  useEffect(() => {
    if (canCall && couponRes.called) {
      const totalWithCoupon = couponRes.data?.amount;
      const coupon = couponRes.data?.coupon;

      if (totalWithCoupon !== undefined) {
        applied_set();
        setTotalWithCoupon(totalWithCoupon);
        setOrderDetails({ ...details, coupon_code: coupon || null });
      } else {
        setTotalWithCoupon(cartTotal);
        setOrderDetails({
          ...details,
          coupon_code: null
        });
      }
    }
  }, [
    couponRes.data?.amount,
    couponRes.data?.coupon,
    cartTotal,
    canCall,
    couponRes.called
  ]);

  useEffect(() => {
    if (canCall) {
      const { error: couponError, params, errorObject } = couponRes;
      if (couponError && canCall) {
        setTotalWithCoupon(cartTotal);
        setOrderDetails({
          ...details,
          coupon_code: null
        });
        if (localCouponValue && params.coupon_code) {
          setError(
            true,
            (errorObject as null | { type?: couponErrorTypeEnum })?.type,
            params.show_banner
          );
          setIsApplied(false);
          timer.execute(time);
        }
      }
    }
  }, [couponRes.error, cartTotal]);

  const setError = (
    hasError: boolean,
    message: couponErrorTypeEnum | undefined,
    hasErrorBanner = false
  ) => {
    setCouponError(hasError);
    setCouponErrorType(message);
    setErrorBanner(hasErrorBanner);
  };

  const applied_set = () => {
    setError(false, undefined);
    if (!disabledCouponInput) {
      setIsApplied(true);
      success_timer.execute(time);
    }
  };

  const timer = useTimeout(
    {
      func: () => {
        setError(false, undefined);
      },
      time,
      call: false
    },
    [couponError]
  );

  const success_timer = useTimeout(
    {
      func: () => {
        setIsApplied(false);
      },
      time,
      call: false
    },
    [isApplied]
  );

  return {
    couponRes,
    isApplied,
    localCouponValue,
    showDiscount,
    setShowDiscount,
    setLocalCouponValue: (value: string) => {
      if (disabledCouponInput) {
        return;
      }

      setLocalCouponValue(value);
    },
    reset: () => {
      setShowDiscount(false);
      setLocalCouponValue(null);
      setOrderDetails({
        ...details,
        coupon_code: null
      });
    },
    setError,
    couponError,
    errorBanner,
    couponErrorType,
    disabledCouponInput
  };
};
